import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import LoteCard from './LoteCard'
import RegulamentoModal from './RegulamentoModal'
import VideoEmbed from './VideoEmbed'

const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8001',
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
})

// Logo/banner chegam da API como caminho relativo ("uploads/...") — vira URL do backend.
const urlDaApi = (url) => (/^https?:\/\//.test(url)
  ? url
  : `${(import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')}/${url.replace(/^\//, '')}`)

export default function CatalogoOnlinePage() {
  const { slug } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showRegulamento, setShowRegulamento] = useState(false)

  useEffect(() => {
    async function fetchCatalogo() {
      try {
        setLoading(true)
        setError(null)
        const response = await publicApi.get(`/api/catalogo-online/${slug}`)
        setData(response.data)
        // Registrar acesso (fire-and-forget)
        publicApi.post(`/api/catalogo-online/${slug}/acesso`, {
          user_agent: navigator.userAgent
        }).catch(() => {})
      } catch (err) {
        if (err.response?.status === 404) {
          setError('not_found')
        } else {
          setError('generic')
        }
      } finally {
        setLoading(false)
      }
    }
    fetchCatalogo()
  }, [slug])

  if (loading) return <LoadingSkeleton />
  if (error === 'not_found') return <NotFoundPage />
  if (error) return <ErrorPage />

  const { organizador, leilao, lotes, regulamento, config } = data
  const corPrimaria = organizador.cor_primaria
  const corSecundaria = organizador.cor_secundaria
  const corHeader = config.cor_header || corPrimaria
  const corFundo = config.cor_fundo || '#f5f5f5'
  const corBotaoInteresse = config.cor_botao_interesse || '#25D366'

  // Contagens
  const isFluxoCavalo = lotes.some(l => Array.isArray(l.animais) && l.animais.length > 0)
  const todosAnimais = lotes.flatMap(l => l.animais || [])
  const totalLotes = lotes.length
  const totalCabecas = isFluxoCavalo ? todosAnimais.length : lotes.reduce((acc, l) => acc + (l.qtd || 0), 0)
  const totalMachos = isFluxoCavalo
    ? todosAnimais.filter(a => a.sexo?.toLowerCase() === 'macho').length
    : lotes.filter(l => l.sexo?.toLowerCase() === 'macho').reduce((acc, l) => acc + (l.qtd || 0), 0)
  const totalFemeas = isFluxoCavalo
    ? todosAnimais.filter(a => a.sexo?.toLowerCase() === 'fêmea' || a.sexo?.toLowerCase() === 'femea').length
    : lotes.filter(l => l.sexo?.toLowerCase() === 'fêmea' || l.sexo?.toLowerCase() === 'femea').reduce((acc, l) => acc + (l.qtd || 0), 0)
  const totalMistos = isFluxoCavalo
    ? todosAnimais.filter(a => a.sexo?.toLowerCase() === 'castrado').length
    : lotes.filter(l => l.sexo?.toLowerCase() === 'misto').reduce((acc, l) => acc + (l.qtd || 0), 0)

  const dataFormatada = (() => {
    if (!leilao.data_leilao) return ''
    const raw = leilao.data_leilao
    const dateStr = raw.includes('T') ? raw : raw + 'T12:00:00'
    const d = new Date(dateStr)
    return isNaN(d) ? raw : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  })()

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: corFundo }}>
      {/* Top Navigation Bar */}
      <nav className="w-full py-2.5 px-4 md:px-6 flex items-center justify-between" style={{ backgroundColor: '#1a1a2e' }}>
        <img src="/assets/logo_rematei_new.png" alt="rematei" className="h-7 object-contain" />
        <a
          href="https://rematei.com.br"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white text-xs font-semibold px-4 py-1.5 rounded transition-all hover:brightness-110"
          style={{ backgroundColor: '#22c55e' }}
        >
          Tenha o rematei no seu leilão!
        </a>
      </nav>

      {/* Hero Header */}
      <header className="w-full" style={{ backgroundColor: corHeader }}>
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <div className="flex flex-col items-center text-center">
            {/* Logo do organizador */}
            {organizador.logo_url && (
              <div className="bg-white rounded-xl p-3 shadow-md mb-4">
                <img
                  src={urlDaApi(organizador.logo_url)}
                  alt={organizador.nome}
                  className="h-16 md:h-20 object-contain"
                />
              </div>
            )}

            {/* Nome do leilão */}
            <h1 className="text-2xl md:text-4xl font-bold text-white leading-tight">
              {leilao.nome}
            </h1>

            {/* Organizador */}
            <p className="text-white/70 text-sm md:text-base mt-1 uppercase tracking-wide">
              {organizador.nome}
            </p>

            {/* Data e local */}
            <p className="text-white/90 text-sm mt-3">
              {dataFormatada}
              {leilao.cidade && config.exibir_totais_sexo && ` – ${totalLotes} lotes | ${totalCabecas} ${isFluxoCavalo ? 'animais' : 'cabeças'}`}
            </p>

            {/* Contagem por sexo */}
            {config.exibir_totais_sexo && (
              <div className="flex flex-wrap items-center justify-center gap-2 mt-3 text-white font-semibold text-sm">
                <span>{totalMachos} Machos</span>
                <span className="text-white/40">|</span>
                <span>{totalFemeas} Fêmeas</span>
                <span className="text-white/40">|</span>
                <span>{totalMistos} {isFluxoCavalo ? 'Castrados' : 'Mistos'}</span>
              </div>
            )}

            {/* Banner do leilão (quando cadastrado) */}
            {leilao.banner_url && (
              <img
                src={urlDaApi(leilao.banner_url)}
                alt={`Banner ${leilao.nome}`}
                className="mt-6 w-auto max-w-full max-h-[28rem] rounded-xl shadow-lg object-contain"
              />
            )}

            {/* Botão regulamento */}
            {regulamento && (
              <div className="mt-5">
                <RegulamentoModal
                  regulamento={regulamento}
                  corPrimaria={corPrimaria}
                  show={showRegulamento}
                  onToggle={() => setShowRegulamento(!showRegulamento)}
                />
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Player de Transmissão */}
      {leilao.link_transmissao && (
        <div className="w-full max-w-5xl mx-auto px-4 md:px-6 pt-6">
          <div className="rounded-xl overflow-hidden shadow-lg">
            <div className="flex items-center gap-2 px-4 py-2.5" style={{ backgroundColor: corHeader }}>
              <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
              <span className="text-white text-sm font-semibold uppercase tracking-wide">Transmissão ao Vivo</span>
            </div>
            <VideoEmbed url={leilao.link_transmissao} autoLoad={true} />
          </div>
        </div>
      )}

      {/* Lotes */}
      <main className="flex-1 w-full max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="space-y-4">
          {lotes.map((lote) => (
            <LoteCard
              key={lote.id}
              lote={lote}
              config={config}
              corPrimaria={corPrimaria}
              corBotaoInteresse={corBotaoInteresse}
              whatsappContatos={config.whatsapp_contatos || []}
              slug={slug}
            />
          ))}
        </div>
        {lotes.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <p className="text-lg">Nenhum lote disponível neste catálogo.</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="w-full py-5 px-4 border-t" style={{ backgroundColor: '#e8e8e8' }}>
        <div className="max-w-5xl mx-auto flex items-center justify-center gap-2 text-gray-500 text-sm">
          <span>Este catálogo foi gerado com o sistema</span>
          <a href="https://rematei.com.br" target="_blank" rel="noopener noreferrer">
            <img src="/assets/logo_rematei_new.png" alt="rematei" className="h-5 object-contain" />
          </a>
        </div>
      </footer>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600"></div>
      <p className="mt-4 text-gray-500 text-sm">Carregando catálogo...</p>
    </div>
  )
}

function NotFoundPage() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="text-center">
        <div className="text-6xl mb-4">🐄</div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Catálogo não encontrado</h1>
        <p className="text-gray-500 max-w-md">
          O catálogo que você está procurando não existe ou não está disponível no momento.
        </p>
      </div>
    </div>
  )
}

function ErrorPage() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="text-center">
        <div className="text-5xl mb-4">⚠️</div>
        <h1 className="text-xl font-bold text-gray-800 mb-2">Erro ao carregar</h1>
        <p className="text-gray-500">
          Ocorreu um erro ao carregar o catálogo. Tente novamente mais tarde.
        </p>
      </div>
    </div>
  )
}
